import { forwardRef } from 'react';
import clsx from 'clsx';
import { ExternalLink } from '@gjensidige/builders-icons';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import type { TrackingProp } from '../types';
import { useTracking, getText } from '../utils';
import styles from './link.module.less';

export type LinkProps<As extends React.ElementType = 'a'> =
  PolymorphicComponentPropWithRef<
    As,
    { icon?: 'auto' | boolean; tracking?: TrackingProp }
  >;

type LinkComponent = <As extends React.ElementType = 'a'>(
  props: LinkProps<As>
) => JSX.Element;

export const Link: LinkComponent = forwardRef(function Link<
  As extends React.ElementType = 'a'
>(
  {
    as,
    children,
    className,
    icon = 'auto',
    onClick,
    tracking = true,
    ...rest
  }: LinkProps<As>,
  ref?: PolymorphicRef<As>
) {
  const Tag = as || 'a';
  const track = useTracking(tracking);
  const handleClick = (event: React.MouseEvent) => {
    track({
      eventCategory: 'link',
      eventAction: 'click',
      eventLabel: getText(event.currentTarget)
    });
    onClick?.(event); // Track before onClick in case URL changes
  };

  if (rest.target === '_blank')
    Object.assign(rest, { rel: 'noreferrer noopener' });

  return (
    <Tag
      className={clsx(styles.link, className)}
      onClick={handleClick}
      ref={ref}
      {...rest}
    >
      {children}
      {rest.target === '_blank' && icon && (
        <ExternalLink className={styles.external} />
      )}
    </Tag>
  );
}) as LinkComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element
